/**
 * PostComposer — create a post, with a type selector, optional book
 * attachment (shared catalog with the Bookshelf feature), poll builder,
 * writing excerpt, and spoiler toggle.
 */

import React, { useState } from 'react';
import { X, Search, Plus, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Textarea } from '@/components/ui/textarea';
import { Button, cx } from '@/components/site/primitives';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useCommunities } from '../context/CommunitiesContext';
import { POST_TYPES, WRITER_POST_TYPES } from '../constants';
import { SEED_BOOKS, DISCOVERABLE_BOOKS } from '@/components/bookshelf/constants/seedBooks';
import BookCoverArt from '@/components/books/BookCoverArt';

const BOOK_CATALOG = [...SEED_BOOKS, ...DISCOVERABLE_BOOKS];

function BookPicker({ book, onSelect, onClear }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const results = query.trim()
    ? BOOK_CATALOG.filter(b => b.title.toLowerCase().includes(query.toLowerCase())).slice(0, 5)
    : [];

  if (book) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-line bg-cream p-2.5">
        <BookCoverArt book={book} className="h-10 w-7 shrink-0" />
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{book.title}</p>
        <button type="button" onClick={onClear} className="shrink-0 rounded-full p-1 text-subtle hover:text-coral" aria-label="Remove book"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }

  return (
    <div className="relative mt-2">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-subtle" />
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Attach a book (optional)…"
        className="w-full rounded-xl border border-line bg-cream py-2 pl-9 pr-3 text-sm text-ink outline-none focus:border-coral"
      />
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-line bg-card shadow-xl">
          {results.map(b => (
            <button
              key={b.id} type="button"
              onClick={() => { onSelect(b); setQuery(''); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-cream"
            >
              <BookCoverArt book={b} className="h-8 w-6 shrink-0" />
              <span className="min-w-0 truncate">{b.title} <span className="text-subtle">— {b.author}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ModalBody({ community, onClose, defaultType }) {
  const { addPost } = useCommunities();
  const typeOptions = community.type === 'writer' ? WRITER_POST_TYPES : POST_TYPES;
  const [type, setType] = useState(defaultType ?? typeOptions[0].value);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [isSpoiler, setIsSpoiler] = useState(false);
  const [book, setBook] = useState(null);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [error, setError] = useState('');

  const isPoll = type === 'poll';
  const isWriting = type === 'share_writing' || type === 'writing_prompt';

  const handleSubmit = () => {
    if (!body.trim() && !title.trim()) return setError('Say something before posting.');
    if (isPoll && pollOptions.filter(o => o.trim()).length < 2) return setError('Add at least two poll options.');

    addPost(community.id, {
      type, title: title.trim() || undefined, body: body.trim(),
      excerpt: isWriting && excerpt.trim() ? excerpt.trim() : undefined,
      book: book ? { title: book.title, author: book.author, tone: book.tone } : undefined,
      poll: isPoll ? { options: pollOptions.filter(o => o.trim()).map(label => ({ label: label.trim(), votes: 0 })) } : undefined,
      isSpoiler,
    });
    onClose();
  };

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-ink">New Post in {community.name}</h2>
      {error && <p className="mt-3 rounded-lg bg-coral/10 px-3 py-2 text-xs font-medium text-coral">{error}</p>}

      <div className="mt-4 flex flex-wrap gap-1.5">
        {typeOptions.map(opt => (
          <button
            key={opt.value} type="button" onClick={() => setType(opt.value)}
            className={cx('rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors', type === opt.value ? 'border-coral bg-coral/10 text-coral' : 'border-line text-ink hover:border-coral/40')}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <input
        value={title} onChange={e => setTitle(e.target.value)}
        placeholder={isPoll ? 'Ask a question…' : 'Title (optional)'}
        className="mt-4 w-full rounded-xl border border-line bg-cream px-3.5 py-2.5 text-sm font-medium text-ink outline-none focus:border-coral"
      />

      <Textarea
        value={body} onChange={e => setBody(e.target.value)}
        placeholder={isWriting ? 'Add context for your writing…' : "What's on your mind?"}
        className="mt-2.5 rounded-xl border-line bg-cream text-sm"
      />

      {isWriting && (
        <Textarea
          value={excerpt} onChange={e => setExcerpt(e.target.value)}
          placeholder="Paste your writing excerpt (optional)…"
          className="mt-2.5 rounded-xl border-line bg-cream font-display text-sm italic"
        />
      )}

      {isPoll && (
        <div className="mt-2.5 space-y-2">
          {pollOptions.map((opt, i) => (
            <input
              key={i} value={opt}
              onChange={e => setPollOptions(prev => prev.map((o, idx) => (idx === i ? e.target.value : o)))}
              placeholder={`Option ${i + 1}`}
              className="w-full rounded-xl border border-line bg-cream px-3.5 py-2 text-sm text-ink outline-none focus:border-coral"
            />
          ))}
          {pollOptions.length < 4 && (
            <button type="button" onClick={() => setPollOptions(prev => [...prev, ''])} className="inline-flex items-center gap-1 text-xs font-semibold text-coral hover:underline">
              <Plus className="h-3.5 w-3.5" /> Add option
            </button>
          )}
        </div>
      )}

      {!isPoll && <BookPicker book={book} onSelect={setBook} onClear={() => setBook(null)} />}

      <label className="mt-3 flex items-center gap-2 text-xs font-medium text-subtle">
        <input type="checkbox" checked={isSpoiler} onChange={e => setIsSpoiler(e.target.checked)} />
        <AlertTriangle className="h-3.5 w-3.5 text-coral" /> Mark as spoiler
      </label>

      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="secondary" icon={false} onClick={onClose}>Cancel</Button>
        <Button variant="primary" icon={false} onClick={handleSubmit}>Post</Button>
      </div>
    </div>
  );
}

export default function PostComposer({ isOpen, onClose, community, defaultType }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  if (!community) return null;

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[88vh] w-[92vw] max-w-lg overflow-y-auto rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">New Post in {community.name}</DialogTitle>
          <ModalBody community={community} onClose={onClose} defaultType={defaultType} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[92vh] overflow-y-auto border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">New Post in {community.name}</DrawerTitle>
        <ModalBody community={community} onClose={onClose} defaultType={defaultType} />
      </DrawerContent>
    </Drawer>
  );
}
